import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ApiService } from '@quorum/api';
import { AuthenticationStateService } from '@quorum/authentication/services';
import { ServiceGroup } from '@quorum/models/xs-resource';
import { Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import {
  MotorMappingEditDialogComponent,
  MotorMappingEditDialogData,
  MotorMappingEditDialogResult,
} from './motor-mapping-edit-dialog.component';
import {
  LinkedDmsService,
  MotorServiceCategory,
  MotorServiceMapping,
  MotorTaxonomyService,
} from './motor-taxonomy.service';

export { MotorServiceCategory, MotorServiceMapping };

const MAX_VISIBLE_LINKED = 2;

@Component({
  selector: 'svc-motor-mapping',
  templateUrl: './motor-mapping.component.html',
  styleUrls: ['./motor-mapping.component.scss'],
})
export class MotorMappingComponent implements OnInit, OnDestroy {
  searchControl = new FormControl('');
  categories: MotorServiceCategory[] = [];
  isLoading = true;
  loadError = false;
  serviceGroupsLoaded = false;

  private destroy$ = new Subject<void>();
  private rooftopId: number | null = null;
  private serviceGroups: ServiceGroup[] = [];
  private existingMappings: { taxonomyId: string; opCodes: string[] }[] = [];

  constructor(
    private motorTaxonomyService: MotorTaxonomyService,
    private dialog: MatDialog,
    private apiService: ApiService,
    private authenticationStateService: AuthenticationStateService
  ) {}

  ngOnInit(): void {
    this.authenticationStateService
      .selectAuthenticatedEmployee()
      .pipe(
        filter((emp) => !!emp),
        take(1)
      )
      .subscribe((emp) => {
        this.rooftopId = emp.rooftopId;
        this.preloadServiceGroups();
        this.preloadMappings();
      });

    this.motorTaxonomyService.preload();
    this.motorTaxonomyService.isLoading$.pipe(takeUntil(this.destroy$)).subscribe((v) => (this.isLoading = v));
    this.motorTaxonomyService.hasError$.pipe(takeUntil(this.destroy$)).subscribe((v) => (this.loadError = v));
    this.motorTaxonomyService.categories$.pipe(takeUntil(this.destroy$)).subscribe((cats) => {
      if (cats !== null) {
        this.categories = cats.map((c) => ({ ...c }));
        this.applyExistingMappings();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private preloadMappings(): void {
    this.apiService
      .get('v/1/fixed-operations/motor/op-code-mappings')
      .pipe(take(1))
      .subscribe((mappings: { taxonomyId: string; opCodes: string[] }[]) => {
        this.existingMappings = mappings;
        this.applyExistingMappings();
      });
  }

  private applyExistingMappings(): void {
    if (!this.existingMappings.length || !this.categories.length) return;
    this.categories = this.categories.map((cat) => ({
      ...cat,
      items: cat.items.map((item) => {
        const mapping = this.existingMappings.find((m) => Number(m.taxonomyId) === item.taxonomyId);
        if (!mapping || !mapping.opCodes.length) return item;
        const linkedServices: LinkedDmsService[] = mapping.opCodes.map((opCode) => {
          const sg = this.serviceGroups.find((s) => s.workOrderLocationOpCode === opCode);
          return { opCode, serviceGroupName: sg?.name ?? opCode };
        });
        return { ...item, linkedServices };
      }),
    }));
  }

  // Service groups are fetched directly via API rather than from ngrx state because
  // the serviceLane state selectors live in @quorum/service-lane-root, which already
  // depends on this library — importing back from it would create a circular dependency.
  private preloadServiceGroups(): void {
    if (!this.rooftopId) return;
    this.apiService
      .get('v/1/fixed-operations/service-groups', {
        params: { rooftopId: `${this.rooftopId},-1`, expired: '0', useFlag: 'A,R' },
      })
      .pipe(take(1))
      .subscribe((groups: ServiceGroup[]) => {
        this.serviceGroups = groups;
        this.serviceGroupsLoaded = true;
        this.applyExistingMappings();
      });
  }

  getMappedCount(category: MotorServiceCategory): number {
    return category.items.filter((item) => item.linkedServices.length > 0).length;
  }

  getFilteredItems(category: MotorServiceCategory): MotorServiceMapping[] {
    const search = (this.searchControl.value || '').toLowerCase().trim();
    if (!search) return category.items;
    return category.items.filter(
      (item) =>
        item.motorServiceName.toLowerCase().includes(search) ||
        item.linkedServices.some(
          (ls) => ls.serviceGroupName.toLowerCase().includes(search) || ls.opCode.toLowerCase().includes(search)
        )
    );
  }

  getLinkedNames(item: MotorServiceMapping): string {
    if (item.linkedServices.length === 0) return '-';
    const names = item.linkedServices.map((ls) => ls.serviceGroupName);
    return names.slice(0, MAX_VISIBLE_LINKED).join(', ');
  }

  getLinkedOverflow(item: MotorServiceMapping): number {
    return Math.max(0, item.linkedServices.length - MAX_VISIBLE_LINKED);
  }

  get allCollapsed(): boolean {
    return this.categories.length > 0 && this.categories.every((c) => c.collapsed);
  }

  toggleAll() {
    const collapse = !this.allCollapsed;
    this.categories.forEach((c) => (c.collapsed = collapse));
  }

  toggleCategory(category: MotorServiceCategory) {
    category.collapsed = !category.collapsed;
  }

  editMapping(item: MotorServiceMapping): void {
    const category = this.categories.find((cat) => cat.items.some((i) => i.taxonomyId === item.taxonomyId));
    if (!category || !this.rooftopId) return;

    const dialogRef = this.dialog.open(MotorMappingEditDialogComponent, {
      width: '675px',
      height: '900px',
      maxHeight: '90vh',
      disableClose: true,
      panelClass: 'motor-mapping-edit-dialog-panel',
      data: {
        item,
        category,
        serviceGroups: this.serviceGroups,
        rooftopId: this.rooftopId,
      } as MotorMappingEditDialogData,
    });

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((result: MotorMappingEditDialogResult | null) => {
        if (!result) return;

        if (result.action === 'save') {
          const opCodes = result.linkedServices.map((ls) => ls.opCode);

          if (this.existingMappings.find((m) => Number(m.taxonomyId) === item.taxonomyId)) {
            this.apiService
              .put(`v/1/fixed-operations/motor/op-code-mappings/${item.taxonomyId}`, { opCodes })
              .pipe(take(1))
              .subscribe(() => {
                this.existingMappings = this.existingMappings.map((m) =>
                  Number(m.taxonomyId) === item.taxonomyId ? { taxonomyId: m.taxonomyId, opCodes } : m
                );
                this.applyLocalMappingChange(item.taxonomyId, result.linkedServices);
              });
          } else {
            this.apiService
              .post('v/1/fixed-operations/motor/op-code-mappings', {
                taxonomyId: `${item.taxonomyId}`,
                opCodes,
              })
              .pipe(take(1))
              .subscribe(() => {
                this.existingMappings = [...this.existingMappings, { taxonomyId: `${item.taxonomyId}`, opCodes }];
                this.applyLocalMappingChange(item.taxonomyId, result.linkedServices);
              });
          }
        } else if (result.action === 'unmap') {
          this.apiService
            .delete(`v/1/fixed-operations/motor/op-code-mappings/${item.taxonomyId}`)
            .pipe(take(1))
            .subscribe(() => {
              this.applyLocalMappingChange(item.taxonomyId, []);
              this.existingMappings = this.existingMappings.filter((m) => Number(m.taxonomyId) !== item.taxonomyId);
            });
        }
      });
  }

  private applyLocalMappingChange(taxonomyId: number, linkedServices: LinkedDmsService[]): void {
    this.categories = this.categories.map((cat) => ({
      ...cat,
      items: cat.items.map((i) => (i.taxonomyId === taxonomyId ? { ...i, linkedServices } : i)),
    }));
  }
}
